import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { FileEdit, Eye, Send, CheckCircle2, History, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  published: "bg-green-100 text-green-700",
  draft: "bg-amber-100 text-amber-700",
  in_review: "bg-blue-100 text-blue-700",
};

const Editor = ({ block, onClose, onChanged }) => {
  const [content, setContent] = useState(block.draft || {});
  const [doc, setDoc] = useState(block);
  const [showHistory, setShowHistory] = useState(false);

  const call = async (fn, msg) => { try { await fn(); toast.success(msg); const { data } = await adminClient().get(`/admin/content/${block.key}`); setDoc(data); setContent(data.draft || {}); onChanged?.(); } catch { toast.error("Action failed."); } };
  const saveDraft = () => call(() => adminClient().put(`/admin/content/${block.key}/draft`, { content }), "Draft saved.");
  const submit = () => call(() => adminClient().post(`/admin/content/${block.key}/submit`), "Submitted for review.");
  const publish = () => call(() => adminClient().post(`/admin/content/${block.key}/publish`), "Published live.");
  const rollback = (v) => call(() => adminClient().post(`/admin/content/${block.key}/rollback/${v}`), `Rolled back to v${v}.`);

  const setField = (f, val) => setContent((c) => ({ ...c, [f]: val }));

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="content-editor">
        <div className="flex items-center justify-between mb-4">
          <div><h2 className="text-xl font-black font-display">{doc.title}</h2><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${STATUS_STYLE[doc.status]}`}>{doc.status}</span></div>
          <button onClick={onClose} className="text-black/40 hover:text-black"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-taven-gold flex items-center gap-1"><FileEdit className="w-3.5 h-3.5" /> Draft (edit)</p>
            {(doc.fields || Object.keys(content)).map((f) => (
              <div key={f}>
                <label className="text-xs font-bold text-black/60 capitalize">{f.replace(/_/g, " ")}</label>
                {typeof content[f] === "boolean" ? (
                  <button onClick={() => setField(f, !content[f])} className={`block mt-1 text-xs font-bold px-3 py-1.5 ${content[f] ? "bg-green-600 text-white" : "bg-black/10"}`} data-testid={`cf-${f}`}>{content[f] ? "Enabled" : "Disabled"}</button>
                ) : (
                  <input value={content[f] ?? ""} onChange={(e) => setField(f, e.target.value)} data-testid={`cf-${f}`} className="w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-taven-gold mt-1" />
                )}
              </div>
            ))}
          </div>
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-1"><Eye className="w-3.5 h-3.5 text-taven-gold" /> Preview</p>
            <div className="border border-black/10 bg-taven-gray/30 p-4 text-sm" data-testid="content-preview">
              {(doc.fields || Object.keys(content)).map((f) => (
                <div key={f} className="mb-1.5"><span className="text-xs text-black/50 capitalize">{f.replace(/_/g, " ")}: </span><span className="font-medium">{String(content[f])}</span></div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Currently live (published): version {Math.max(...(doc.versions||[{version:1}]).map(v=>v.version))}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-6 border-t border-black/10 pt-4">
          <button onClick={saveDraft} data-testid="save-draft" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-taven-black text-white px-4 py-2.5 hover:bg-taven-orange"><FileEdit className="w-3.5 h-3.5" /> Save Draft</button>
          <button onClick={submit} data-testid="submit-review" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-blue-600 text-white px-4 py-2.5 hover:bg-blue-700"><Send className="w-3.5 h-3.5" /> Submit for Review</button>
          <button onClick={publish} data-testid="publish-content" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-green-600 text-white px-4 py-2.5 hover:bg-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> Publish Live</button>
          <button onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-black/15 px-4 py-2.5 hover:border-taven-gold"><History className="w-3.5 h-3.5" /> History ({(doc.versions||[]).length})</button>
        </div>

        {showHistory && (
          <div className="mt-4 border border-black/10" data-testid="version-history">
            {[...(doc.versions||[])].reverse().map((v) => (
              <div key={v.version} className="flex items-center justify-between px-4 py-2.5 border-b border-black/5 text-sm">
                <div><span className="font-bold">v{v.version}</span> <span className="text-muted-foreground">· {new Date(v.published_at).toLocaleString()} · {v.published_by}{v.note ? ` · ${v.note}` : ""}</span></div>
                <button onClick={() => rollback(v.version)} data-testid={`rollback-${v.version}`} className="flex items-center gap-1 text-xs font-bold text-taven-gold hover:underline"><RotateCcw className="w-3.5 h-3.5" /> Rollback</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminUpdateCenter() {
  const [blocks, setBlocks] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => adminClient().get("/admin/content").then(({ data }) => setBlocks(data.blocks || [])).catch(() => setBlocks([]));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-update-center">
      <div className="flex items-center gap-2 mb-1"><FileEdit className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Update Center</h1></div>
      <p className="text-sm text-muted-foreground mb-6">Edit public site content safely: <strong>Draft → Preview → Review → Publish</strong>, with full version history and one-click rollback. Only published content appears on the live site.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {blocks.map((b) => (
          <button key={b.key} onClick={() => setEditing(b)} data-testid={`content-block-${b.key}`} className="text-left border border-black/10 bg-white p-4 hover:border-taven-gold transition-colors">
            <div className="flex items-center justify-between"><span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{b.group}</span><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${STATUS_STYLE[b.status]}`}>{b.status}</span></div>
            <p className="font-black font-display mt-2">{b.title}</p>
            <p className="text-xs text-muted-foreground mt-1">v{Math.max(...(b.versions||[{version:1}]).map(v=>v.version))} live · {(b.versions||[]).length} versions</p>
          </button>
        ))}
      </div>

      {editing && <Editor block={editing} onClose={() => setEditing(null)} onChanged={load} />}
    </div>
  );
}
